/**
 * CS-5B — WHO GOVERNS A DOCUMENT TYPE'S REUSABILITY (docs/mds/11 Amendment CS5-M3; Q-CS5-13a).
 *
 * A MIRROR of the server's rule (`documentCatalogService.mayGovernReusability`), so the Document Reusability page
 * shows actions only to people the server would accept and the navigation entry only to departments that govern
 * something. The SERVER remains the authority and re-checks every change.
 *
 *   Master Admin                          every type
 *   Registration cum Sales Department Admin   Identity (KYC) and Address Proof types
 *   any other Department Admin            only types whose verifying department is theirs
 *
 * Dependency-free on purpose: a backend test imports this module to prove it matches the server rule.
 */

export const REUSE_GOVERNED_BY_RS = Object.freeze(["A Identity (KYC)", "B Address Proof"]);
export const RS_DEPARTMENT = "Registration cum Sales";
const MASTER_ADMIN = "Master Admin";
const DEPARTMENT_ADMIN = "Department Admin";

const isMaster = (user) => user?.designation === MASTER_ADMIN;
const isDeptAdmin = (user) => user?.designation === DEPARTMENT_ADMIN;

/** May this person change THIS catalogue row's reusability? */
export function mayGovernReusability(user, row) {
  if (!row) return false;
  if (isMaster(user)) return true;
  if (!isDeptAdmin(user)) return false;
  if (REUSE_GOVERNED_BY_RS.includes(row.category)) return user?.department === RS_DEPARTMENT;
  return !!row.verifyingDepartment && user?.department === row.verifyingDepartment;
}

/** "Governed by" in words, for one catalogue row. */
export function governedByLabel(row) {
  if (REUSE_GOVERNED_BY_RS.includes(row?.category)) return "Master Admin · R&S Department Admin";
  if (row?.verifyingDepartment) return `Master Admin · ${row.verifyingDepartment} Admin`;
  return "Master Admin only";
}

/** The departments whose Department Admin governs at least one row of this catalogue. */
export function governingDepartments(catalogue) {
  const set = new Set();
  for (const row of catalogue || []) {
    if (REUSE_GOVERNED_BY_RS.includes(row.category)) set.add(RS_DEPARTMENT);
    else if (row.verifyingDepartment) set.add(row.verifyingDepartment);
  }
  return set;
}

/**
 * Does this person get the "Document Reusability" configuration entry (owner decision 2026-09-28)?
 *   R&S Department Admin     always (their department governs Identity and Address Proof)
 *   other Department Admins  only when their department actually governs at least one type — `governing` is the
 *                            set from `governingDepartments`, known only once the catalogue has been read
 *   Administration Admin     never (governance is not an Administration capability)
 *   Master Admin             not here — the Master reaches it from System → Configuration → Platform
 */
export function canSeeReusabilityEntry(user, governing) {
  if (!isDeptAdmin(user)) return false;
  if (user?.department === RS_DEPARTMENT) return true;
  if (user?.department === "Administration") return false;
  return !!governing && governing.has(user?.department);
}

/** The category heading without its catalogue letter: "A Identity (KYC)" → "Identity (KYC)". */
export const categoryHeading = (category) => String(category || "").replace(/^[A-Z]\s+/, "");
